import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { fetchMatchupPreview } from '../../api/matchupPreview';
import MatchupHeader from './MatchupHeader';
import ProbablePitchers from './ProbablePitchers';
import TeamSeasonComparison from './TeamSeasonComparison';
import KeyPlayersToWatch from './KeyPlayersToWatch';
import MatchupPowerRating from './MatchupPowerRating';

const { FiX, FiActivity, FiBarChart2, FiUsers, FiZap } = FiIcons;

const TABS = [
  { id: 'overview', label: 'Overview', icon: FiActivity },
  { id: 'stats', label: 'Season Stats', icon: FiBarChart2 },
  { id: 'players', label: 'Players', icon: FiUsers },
  { id: 'edge', label: 'Power Rating', icon: FiZap },
];

const MatchupPreviewModal = ({ game, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (!game?.gamePk) return;
    setLoading(true);
    fetchMatchupPreview(game.gamePk)
      .then(setData)
      .catch(() => setError('Could not load matchup data.'))
      .finally(() => setLoading(false));
  }, [game?.gamePk]);

  const awayAbbr = game?.teams?.away?.team?.abbreviation || 'AWY';
  const homeAbbr = game?.teams?.home?.team?.abbreviation || 'HME';
  const gameData = data?.feed?.gameData || null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-6"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      >
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
        <motion.div
          className="relative w-full max-w-2xl bg-[#1e3a29] border-4 border-[#d8ceb3] shadow-2xl z-10 max-h-[94vh] flex flex-col"
          initial={{ scale: 0.92, y: 24 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 24 }}
          transition={{ type: 'spring', stiffness: 280, damping: 24 }}
        >
          {/* Header */}
          <div className="bg-[#386646] border-b-2 border-[#658d71] p-3 sm:p-4 flex items-center justify-between shrink-0">
            <div>
              <p className="text-[9px] tracking-widest uppercase text-[#a9c9b1] font-bold">Matchup Preview</p>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-base sm:text-xl font-black text-white tracking-widest">{awayAbbr}</span>
                <span className="text-lg sm:text-2xl font-black text-[#658d71]">@</span>
                <span className="text-base sm:text-xl font-black text-white tracking-widest">{homeAbbr}</span>
              </div>
            </div>
            <button onClick={onClose} className="p-2 border border-[#658d71] text-[#d8ceb3] hover:bg-[#658d71] transition-colors">
              <SafeIcon icon={FiX} size={18} />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b-2 border-[#658d71] shrink-0 overflow-x-auto">
            {TABS.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1 px-3 sm:px-4 py-2.5 text-[9px] sm:text-[10px] font-bold uppercase tracking-widest transition-colors whitespace-nowrap border-r border-[#658d71] last:border-0 ${activeTab === tab.id ? 'bg-[#386646] text-[#d8ceb3]' : 'text-[#a9c9b1] hover:text-white hover:bg-[rgba(255,255,255,0.04)]'}`}>
                <SafeIcon icon={tab.icon} size={10} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-3 sm:p-5">
            {loading && (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <div className="w-8 h-8 border-2 border-[#658d71] border-t-[#d8ceb3] rounded-full animate-spin" />
                <span className="text-[#a9c9b1] text-xs tracking-widest uppercase">Loading matchup...</span>
              </div>
            )}
            {error && <p className="text-center text-[#e6a8a8] text-sm py-8">{error}</p>}
            {!loading && !error && (
              <AnimatePresence mode="wait">
                <motion.div key={activeTab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.18 }} className="flex flex-col gap-4">
                  {activeTab === 'overview' && (
                    <>
                      <MatchupHeader gameData={gameData} />
                      <ProbablePitchers gameData={gameData} />
                    </>
                  )}
                  {activeTab === 'stats' && (
                    <TeamSeasonComparison awayStats={data?.awayStats} homeStats={data?.homeStats} awayAbbr={awayAbbr} homeAbbr={homeAbbr} />
                  )}
                  {activeTab === 'players' && (
                    <KeyPlayersToWatch awayRoster={data?.awayRoster} homeRoster={data?.homeRoster} awayAbbr={awayAbbr} homeAbbr={homeAbbr} />
                  )}
                  {activeTab === 'edge' && (
                    <MatchupPowerRating awayStats={data?.awayStats} homeStats={data?.homeStats} awayAbbr={awayAbbr} homeAbbr={homeAbbr} />
                  )}
                </motion.div>
              </AnimatePresence>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default MatchupPreviewModal;